/** Content → blog categories, tags, series (/sites/:id/blog-terms) and authors (/sites/:id/blog-authors). */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Segmented, Space, Table, Avatar } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { listOf, toastErr, useSite } from './common';
import CoverPicker from './blog/CoverPicker';

const useCrud = (path, params) => {
    const { api } = useSite();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const key = JSON.stringify(params || {});
    const load = useCallback(() => {
        setLoading(true);
        api.get(path, params).then((d) => setItems(listOf(d))).catch(toastErr).finally(() => setLoading(false));
    }, [api, path, key]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);
    const save = async (id, body) => { await (id ? api.put(`${path}/${id}`, body) : api.post(path, body)); load(); };
    const del = async (id) => { try { await api.del(`${path}/${id}`); load(); } catch (e) { toastErr(e); } };
    return { items, loading, save, del };
};

const FormModal = ({ row, title, onClose, onSave, children, form }) => {
    useEffect(() => { if (row) { form.resetFields(); form.setFieldsValue(row); } }, [row, form]);
    const ok = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        try { await onSave(v); onClose(); } catch (e) { toastErr(e); }
    };
    return <Modal open={!!row} title={title} onCancel={onClose} onOk={ok} forceRender><Form form={form} layout="vertical">{children}</Form></Modal>;
};

export const BlogTerms = () => {
    const { t } = useTranslation();
    const [kind, setKind] = useState('category');
    const { items, loading, save, del } = useCrud('/post-terms', { kind });
    const [row, setRow] = useState(null);
    const [form] = Form.useForm();
    return (
        <Card title={t('Categories and tags')} extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setRow({ name: '', slug: '', description: '' })}>{t('Add')}</Button>}>
            <Segmented style={{ marginBottom: 12 }} value={kind} onChange={setKind} options={[{ value: 'category', label: t('Categories') }, { value: 'tag', label: t('Tags') }, { value: 'series', label: t('Series') }]} />
            <Table rowKey="id" size="small" loading={loading} dataSource={items} pagination={false} scroll={{ x: 520 }} columns={[
                { title: t('Name'), dataIndex: 'name', render: (v, r) => <a onClick={() => setRow(r)}>{v}</a> },
                { title: t('URL handle'), dataIndex: 'slug' },
                { title: t('Posts'), dataIndex: 'posts_count', width: 80 },
                { key: 'x', width: 50, render: (_, r) => <Popconfirm title={t('Delete?')} okButtonProps={{ danger: true }} onConfirm={() => del(r.id)}><Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={t('Delete')} /></Popconfirm> },
            ]} />
            <FormModal row={row} form={form} title={row?.id ? t('Edit') : t('Add')} onClose={() => setRow(null)} onSave={(v) => save(row.id, row.id ? v : { ...v, kind })}>
                <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input maxLength={191} /></Form.Item>
                <Form.Item name="slug" label={t('URL handle')}><Input maxLength={191} /></Form.Item>
                <Form.Item name="description" label={t('Description')} style={{ marginBottom: 0 }}><Input.TextArea autoSize={{ minRows: 2 }} maxLength={2000} /></Form.Item>
            </FormModal>
        </Card>
    );
};

export const BlogAuthors = () => {
    const { t } = useTranslation();
    const { siteId } = useSite();
    const { items, loading, save, del } = useCrud('/authors');
    const [row, setRow] = useState(null);
    const [form] = Form.useForm();
    const avatar = Form.useWatch('avatar', form);
    return (
        <Card title={t('Authors')} extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setRow({ name: '', slug: '', bio: '', avatar: null, links: [] })}>{t('Add')}</Button>}>
            <Table rowKey="id" size="small" loading={loading} dataSource={items} pagination={false} scroll={{ x: 520 }} columns={[
                { title: t('Name'), dataIndex: 'name', render: (v, r) => <Space><Avatar size="small" src={r.avatar?.src}>{(v || '?')[0]}</Avatar><a onClick={() => setRow(r)}>{v}</a></Space> },
                { title: t('URL handle'), dataIndex: 'slug' },
                { title: t('Posts'), dataIndex: 'posts_count', width: 80 },
                { key: 'x', width: 50, render: (_, r) => <Popconfirm title={t('Delete?')} okButtonProps={{ danger: true }} onConfirm={() => del(r.id)}><Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={t('Delete')} /></Popconfirm> },
            ]} />
            <FormModal row={row} form={form} title={row?.id ? t('Edit') : t('Add')} onClose={() => setRow(null)} onSave={(v) => save(row.id, v)}>
                <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input maxLength={191} /></Form.Item>
                <Form.Item name="slug" label={t('URL handle')}><Input maxLength={191} /></Form.Item>
                <Form.Item name="avatar" label={t('Photo')}>
                    <CoverPicker siteId={siteId} src={avatar?.src} onChange={(m) => form.setFieldValue('avatar', m ? { src: m.url, alt: '' } : null)} />
                </Form.Item>
                <Form.Item name="bio" label={t('Bio')}><Input.TextArea autoSize={{ minRows: 2 }} maxLength={2000} /></Form.Item>
                <Form.List name="links">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((fl) => (
                                <Space.Compact key={fl.key} style={{ display: 'flex', marginBottom: 6 }}>
                                    <Form.Item name={[fl.name, 'label']} noStyle><Input placeholder={t('Label')} style={{ width: '35%' }} /></Form.Item>
                                    <Form.Item name={[fl.name, 'url']} noStyle><Input placeholder="https://" /></Form.Item>
                                    <Button icon={<DeleteOutlined />} aria-label={t('Remove')} onClick={() => remove(fl.name)} />
                                </Space.Compact>
                            ))}
                            <Button size="small" icon={<PlusOutlined />} onClick={() => add({ label: '', url: '' })}>{t('Add link')}</Button>
                        </>
                    )}
                </Form.List>
            </FormModal>
        </Card>
    );
};
